import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import signalRService from "../../Services/SignalR/SignalRService";
import { getToken } from "../../Utils/Auth";
import { PortalTypeEnum } from "../../Utils/Common/Enums/Enums";

/**
 * Hook to manage SignalR connection
 * Connects after login and disconnects on logout
 */
export const useSignalR = () => {
  const token = getToken();
  const isInitializedRef = useRef(false);
  const initializationPromiseRef = useRef<Promise<void> | null>(null);
  
  // Get portal type from Redux store
  const portalType = useSelector((state: any) => 
    state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.adminType || 
    state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.portalType
  );

  useEffect(() => {
    // Only connect if we have a token, haven't initialized yet, and PortalType is Admin
    if (token && !isInitializedRef.current && portalType === PortalTypeEnum.Admin) {
      // Prevent multiple simultaneous initializations
      if (!initializationPromiseRef.current) {
        isInitializedRef.current = true;
        initializationPromiseRef.current = signalRService
          .initializeConnection()
          .then(() => {
            console.log("SignalR connections initialized");
          })
          .catch((error) => {
            console.error("Failed to initialize SignalR:", error);
            isInitializedRef.current = false; // Allow retry on error
          })
          .finally(() => {
            initializationPromiseRef.current = null;
          });
      }
    }

    // Cleanup on unmount, when token is removed, or when portalType changes away from Admin
    return () => {
      if ((!token || portalType !== PortalTypeEnum.Admin) && isInitializedRef.current) {
        signalRService.disconnect().then(() => {
          isInitializedRef.current = false;
          initializationPromiseRef.current = null;
        });
      }
    };
  }, [token, portalType]);

  return {
    isConnected: signalRService.isConnected(),
  };
};

export default useSignalR;

